import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, ArrowRight, X } from 'lucide-react'
import { useUser } from '../../contexts/UserContext'
import V2BoardAPI from '../../services/api'
import { getOrders } from '../../services/order'

const UnpaidOrderBanner = () => {
  const { user } = useUser()
  const navigate = useNavigate()
  const location = useLocation()
  const [unpaidCount, setUnpaidCount] = useState(0)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!user?.token || !user?.baseUrl) return

    let cancelled = false
    const api = new V2BoardAPI(user.baseUrl, user.token)

    const fetchUnpaid = async () => {
      try {
        const orders = await getOrders(api)
        if (cancelled) return
        const unpaid = Array.isArray(orders)
          ? orders.filter((o) => o?.status === 0).length
          : 0
        setUnpaidCount(unpaid)
      } catch {
        // silently ignore — banner is non-critical
      }
    }

    fetchUnpaid()
    return () => {
      cancelled = true
    }
  }, [user?.token, user?.baseUrl, location.pathname])

  // Reset dismissal when count changes (so a new unpaid order re-shows the banner)
  useEffect(() => {
    setDismissed(false)
  }, [unpaidCount])

  const shouldShow =
    unpaidCount > 0 && !dismissed && location.pathname !== '/orders'

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="px-4 lg:px-6 pt-3"
        >
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="group w-full flex items-center gap-3 rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 px-4 py-3 shadow-sm hover:shadow-md transition-all text-left"
          >
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-amber-500/15 dark:bg-amber-500/25 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <AlertCircle className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                你有 {unpaidCount} 个待支付订单
              </p>
              <p className="text-xs text-amber-700/80 dark:text-amber-200/70 truncate">
                点击前往订单页面完成支付
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-amber-600 dark:text-amber-400 transition-transform group-hover:translate-x-1" />
            <span
              role="button"
              tabIndex={0}
              aria-label="关闭提醒"
              onClick={(e) => {
                e.stopPropagation()
                setDismissed(true)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  setDismissed(true)
                }
              }}
              className="flex-shrink-0 p-1 rounded-md text-amber-700/70 dark:text-amber-200/60 hover:bg-amber-500/10 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default UnpaidOrderBanner
